/*
 * Trace storefront signals for Shopify.
 *
 * This is the canonical storefront collector. It records a compact, structured
 * journey. Its optional live view sends short-lived, sanitized visual frames
 * that are never persisted and never include form values, account or checkout.
 */
(function traceTracker(config) {
  'use strict';

  const TRACKER_VERSION = '2.1.2';
  const SCHEMA_VERSION = 2;
  const eventEndpoint = config && config.endpoint ? config.endpoint : '/api/events';
  const liveViewEnabled = Boolean(config && config.liveView === true);
  const liveEndpoint = config && config.liveEndpoint
    ? config.liveEndpoint
    : eventEndpoint.replace(/\/events(?:\?.*)?$/, '/live-frame');
  const heartbeatIntervalMs = Math.max(2000, Number(config.heartbeatIntervalMs || 3000));
  const liveIntervalMs = Math.max(1000, Number(config.liveIntervalMs || 1500));
  const consentRequired = !config || config.consentRequired !== false;
  const respectGpc = !config || config.respectGpc !== false;
  const sessionIdleMs = Math.max(5 * 60 * 1000, Number(config.sessionIdleMs || 30 * 60 * 1000));
  const sessionMaxMs = Math.max(sessionIdleMs, Number(config.sessionMaxMs || 4 * 60 * 60 * 1000));
  const sessionStateKey = 'trace_session_v2';
  const visitorKey = 'trace_visitor_id_v2';
  const errorWindowMs = 60 * 1000;
  const SHOPIFY_HOSTS = /(^|\.)(shopify\.com|shopifycdn\.com|myshopify\.com|shop\.app)$/i;

  let started = false;
  let idle = false;
  let idleTimer = null;
  let heartbeatTimer = null;
  let retryTimer = null;
  let liveFrameTimer = null;
  let livePointer = { x: -1, y: -1 };
  let pendingRequests = [];
  let lastClockAt = Date.now();
  let pageViewId = randomId('page');
  let cartSnapshot = emptyCart();
  let nativeFetchRequest = null;
  const semanticSentAt = new Map();
  const requestErrors = new Map();

  function randomId(prefix) {
    const value = globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function'
      ? globalThis.crypto.randomUUID().replace(/-/g, '').slice(0, 20)
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
    return `${prefix}-${value}`;
  }

  function safeText(value, limit) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, limit || 120);
  }

  function storageGet(storage, key) {
    try { return storage.getItem(key); } catch { return null; }
  }

  function storageSet(storage, key, value) {
    try { storage.setItem(key, value); return true; } catch { return false; }
  }

  function isInternalHost(host) {
    if (!host) return true;
    const self = location.hostname.replace(/^www\./i, '').toLowerCase();
    const other = String(host).replace(/^www\./i, '').toLowerCase();
    if (other === self) return true;
    if (other.endsWith(`.${self}`) || self.endsWith(`.${other}`)) return true;
    return SHOPIFY_HOSTS.test(other);
  }

  function firstTouch() {
    const query = new URLSearchParams(location.search);
    let referrer = '';
    let referrerHost = '';
    try {
      const parsed = document.referrer ? new URL(document.referrer) : null;
      if (parsed && !isInternalHost(parsed.hostname)) {
        referrer = parsed.href.slice(0, 500);
        referrerHost = parsed.hostname.replace(/^www\./i, '').slice(0, 120);
      }
    } catch {
      referrer = '';
    }
    const source = safeText(query.get('utm_source') || referrerHost || 'direct', 80).toLowerCase();
    return {
      source,
      medium: safeText(query.get('utm_medium'), 80),
      campaign: safeText(query.get('utm_campaign') || query.get('utm_id') || query.get('campaign_id'), 120),
      content: safeText(query.get('utm_content') || query.get('ad_id'), 120),
      term: safeText(query.get('utm_term'), 120),
      referrer,
      referrerHost,
      clickIdPresent: Boolean(query.get('fbclid') || query.get('gclid') || query.get('ttclid') || query.get('msclkid')),
      landingPath: `${location.pathname}${location.search}`.slice(0, 1000)
    };
  }

  function newSessionState(now, reason) {
    return {
      id: randomId('sess'),
      startedAt: now,
      lastActivityAt: now,
      seq: 0,
      opened: false,
      checkoutSent: false,
      activeMs: 0,
      visibleMs: 0,
      firstTouch: firstTouch(),
      pendingCart: null,
      reason: reason || 'new_tab'
    };
  }

  function readSessionState() {
    const now = Date.now();
    try {
      const parsed = JSON.parse(storageGet(sessionStorage, sessionStateKey) || 'null');
      if (!parsed || !parsed.id || !Number(parsed.startedAt) || !Number(parsed.lastActivityAt)) {
        return newSessionState(now, 'new_tab');
      }
      if (now - Number(parsed.lastActivityAt) > sessionIdleMs) return newSessionState(now, 'idle_timeout');
      if (now - Number(parsed.startedAt) > sessionMaxMs) return newSessionState(now, 'max_duration');
      return {
        ...parsed,
        seq: Number(parsed.seq || 0),
        activeMs: Number(parsed.activeMs || 0),
        visibleMs: Number(parsed.visibleMs || 0),
        firstTouch: parsed.firstTouch || firstTouch()
      };
    } catch {
      return newSessionState(now, 'invalid_state');
    }
  }

  let session = readSessionState();
  const visitorId = storageGet(localStorage, visitorKey) || randomId('visitor');
  storageSet(localStorage, visitorKey, visitorId);

  function persistSession() {
    storageSet(sessionStorage, sessionStateKey, JSON.stringify(session));
  }

  function emptyCart() {
    return { cartValue: 0, cartCount: 0, lineCount: 0, currency: '', items: [] };
  }

  function allowed() {
    if (respectGpc && navigator.globalPrivacyControl === true) return false;
    if (!consentRequired || window.__TRACE_CONSENT__ === true) return true;
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) return false;
    if (typeof privacy.analyticsProcessingAllowed === 'function') return privacy.analyticsProcessingAllowed() === true;
    if (typeof privacy.userCanBeTracked === 'function') return privacy.userCanBeTracked() === true;
    return false;
  }

  function pageType() {
    const path = location.pathname || '/';
    if (/\/products\//i.test(path)) return 'product';
    if (/\/collections\//i.test(path)) return 'collection';
    if (/\/cart\/?$/i.test(path)) return 'cart';
    if (/\/search\/?$/i.test(path)) return 'search';
    if (/\/account(?:\/|$)/i.test(path)) return 'account';
    if (/\/checkout|\/thank_you|\/orders\//i.test(path)) return 'checkout';
    return path === '/' || /^\/(?:en|es)\/?$/i.test(path) ? 'home' : 'other';
  }

  function updateClocks(now) {
    const moment = Number(now || Date.now());
    const elapsed = Math.max(0, Math.min(15000, moment - lastClockAt));
    if (!document.hidden) {
      session.visibleMs += elapsed;
      if (!idle) session.activeMs += elapsed;
    }
    lastClockAt = moment;
    persistSession();
  }

  function basePayload(clientAt) {
    return {
      schemaVersion: SCHEMA_VERSION,
      trackerVersion: TRACKER_VERSION,
      sessionId: session.id,
      visitorId,
      pageViewId,
      sessionStartedAt: session.startedAt,
      clientAt,
      path: `${location.pathname}${location.search}`.slice(0, 1000),
      pageTitle: safeText(document.title, 240),
      referrer: session.firstTouch.referrer || '',
      attribution: session.firstTouch,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      device: navigator.userAgent || '',
      deviceClass: window.matchMedia && window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop',
      locale: safeText(config.locale || document.documentElement.lang, 20),
      themeId: safeText(config.themeId, 40),
      themeRole: safeText(config.themeRole || 'live', 20),
      isPreview: Boolean(config.isPreview || new URLSearchParams(location.search).has('preview_theme_id') || document.querySelector('[data-shopify-preview-bar]')),
      pageType: pageType(),
      activeMs: Math.round(session.activeMs),
      visibleMs: Math.round(session.visibleMs)
    };
  }

  function scheduleRetry() {
    if (retryTimer || !pendingRequests.length) return;
    retryTimer = setTimeout(async () => {
      retryTimer = null;
      const queue = pendingRequests.splice(0, 10);
      for (const item of queue) {
        try {
          const response = await fetch(item.url, {
            method: 'POST', mode: 'cors', credentials: 'omit',
            headers: { 'Content-Type': 'application/json' },
            body: item.body,
            keepalive: item.body.length < 60000
          });
          if (!response.ok) throw new Error(`Trace returned ${response.status}`);
        } catch {
          if (item.attempt < 3) pendingRequests.push({ ...item, attempt: item.attempt + 1 });
        }
      }
      if (pendingRequests.length) scheduleRetry();
    }, 1800);
  }

  async function postJson(url, body) {
    try {
      const response = await fetch(url, {
        method: 'POST', mode: 'cors', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' }, body,
        keepalive: body.length < 60000
      });
      if (!response.ok) throw new Error(`Trace returned ${response.status}`);
      return true;
    } catch {
      pendingRequests.push({ url, body, attempt: 1 });
      if (pendingRequests.length > 60) pendingRequests = pendingRequests.slice(-60);
      scheduleRetry();
      return false;
    }
  }

  function maskLiveText(value) {
    return safeText(value, 240)
      .replace(/[\w.+-]+@[\w.-]+\.[a-z]{2,}/gi, '[email oculto]')
      .replace(/(?:\+?\d[\s().-]*){7,}/g, '[dato oculto]');
  }

  function sensitiveLivePath() {
    return /\/(?:account|checkout|orders|challenge|password)(?:\/|$)/i.test(location.pathname);
  }

  function sensitiveLiveElement(element) {
    if (!element || typeof element.closest !== 'function') return true;
    if (element.closest('form, input, textarea, select, option, [contenteditable], [data-private], [data-sensitive]')) return true;
    let current = element;
    for (let depth = 0; current && depth < 4; depth += 1, current = current.parentElement) {
      const marker = `${current.id || ''} ${typeof current.className === 'string' ? current.className : ''}`;
      if (/(customer|account|profile|address|login|register|password|payment|checkout)/i.test(marker)) return true;
    }
    return false;
  }

  function visibleLiveRect(element) {
    const rect = element.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2 || rect.bottom < 0 || rect.right < 0 || rect.top > innerHeight || rect.left > innerWidth) return null;
    const style = getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity || 1) <= .02) return null;
    return { rect, style };
  }

  function liveGeometry(rect) {
    return {
      x: Math.round(rect.left * 10) / 10,
      y: Math.round(rect.top * 10) / 10,
      width: Math.round(rect.width * 10) / 10,
      height: Math.round(rect.height * 10) / 10
    };
  }

  function liveStyle(style) {
    return {
      color: style.color,
      background: style.backgroundColor,
      borderColor: style.borderColor,
      borderWidth: parseFloat(style.borderWidth) || 0,
      borderRadius: parseFloat(style.borderRadius) || 0,
      fontSize: parseFloat(style.fontSize) || 14,
      fontWeight: parseInt(style.fontWeight, 10) || 400,
      textAlign: ['left', 'center', 'right'].includes(style.textAlign) ? style.textAlign : 'left',
      objectFit: ['contain', 'cover', 'fill'].includes(style.objectFit) ? style.objectFit : 'cover',
      opacity: Number(style.opacity || 1)
    };
  }

  function collectLiveNodes() {
    const nodes = [];
    const seen = new Set();
    const push = (element, type, text, src) => {
      if (nodes.length >= 180 || sensitiveLiveElement(element)) return;
      const visible = visibleLiveRect(element);
      if (!visible) return;
      const geometry = liveGeometry(visible.rect);
      const key = `${type}:${geometry.x}:${geometry.y}:${geometry.width}:${geometry.height}:${text || src || ''}`;
      if (seen.has(key)) return;
      seen.add(key);
      nodes.push({ type, ...geometry, ...liveStyle(visible.style), text: maskLiveText(text), src: src || '' });
    };

    document.querySelectorAll('header, nav, main, section, article, aside, footer, [role="dialog"], [class*="drawer" i], [class*="banner" i]').forEach((element) => {
      const visible = visibleLiveRect(element);
      if (!visible || visible.rect.width < 60 || visible.rect.height < 24) return;
      if (visible.style.backgroundColor === 'rgba(0, 0, 0, 0)' && visible.style.borderWidth === '0px') return;
      push(element, 'block', '', '');
    });

    document.querySelectorAll('img').forEach((element) => {
      const src = element.currentSrc || element.src || '';
      if (/^https:\/\//i.test(src)) push(element, 'image', '', src.slice(0, 1000));
    });

    document.querySelectorAll('h1, h2, h3, h4, p, a, button, li, label, [role="button"], [class*="price" i]').forEach((element) => {
      const ownText = Array.from(element.childNodes || [])
        .filter((node) => node.nodeType === 3)
        .map((node) => node.textContent || '').join(' ').trim();
      const text = ownText || (/^(A|BUTTON|H1|H2|H3|H4|P|LI)$/.test(element.tagName) ? element.textContent : '');
      if (maskLiveText(text)) push(element, 'text', text, '');
    });
    return nodes;
  }

  async function sendLiveFrame(active) {
    if (!liveViewEnabled || !started || !allowed()) return;
    const enabled = active !== false && !document.hidden && !sensitiveLivePath();
    const body = JSON.stringify(enabled ? {
      sessionId: session.id,
      active: true,
      path: `${location.pathname}${location.search}`.slice(0, 1000),
      pageTitle: safeText(document.title, 240),
      viewportWidth: innerWidth,
      viewportHeight: innerHeight,
      background: getComputedStyle(document.body).backgroundColor,
      pointerX: livePointer.x,
      pointerY: livePointer.y,
      nodes: collectLiveNodes()
    } : { sessionId: session.id, active: false });
    try {
      // Bypass Trace's storefront network wrapper: a best-effort live frame is
      // internal telemetry, never a storefront failure worth recording.
      const request = nativeFetchRequest || window.fetch.bind(window);
      await request(liveEndpoint, {
        method: 'POST', mode: 'cors', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' }, body,
        keepalive: body.length < 60000
      });
    } catch {
      // Live frames are deliberately never queued or replayed later.
    }
  }

  function startLiveView() {
    if (!liveViewEnabled || sensitiveLivePath()) return;
    setTimeout(() => sendLiveFrame(true), 900);
    liveFrameTimer = setInterval(() => sendLiveFrame(true), liveIntervalMs);
  }

  function send(type, detail, extra, options) {
    if (!allowed()) return false;
    const clientAt = Date.now();
    updateClocks(clientAt);
    session.seq += 1;
    const payload = {
      ...basePayload(clientAt),
      eventId: `${session.id}-${clientAt}-${session.seq}`,
      sequence: session.seq,
      type,
      detail: safeText(detail, 500),
      ...(extra || {})
    };
    persistSession();
    const body = JSON.stringify(payload);
    const priority = options && options.priority === true;
    if ((priority || ['page_exit', 'session_end', 'visibility_hidden', 'identify', 'checkout_start', 'add_to_cart'].includes(type))
      && navigator.sendBeacon && body.length < 60000) {
      if (navigator.sendBeacon(eventEndpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' }))) return true;
    }
    postJson(eventEndpoint, body);
    return true;
  }

  function semanticAllowed(type, key, windowMs) {
    const signature = `${type}:${key || ''}`;
    const now = Date.now();
    if (now - Number(semanticSentAt.get(signature) || 0) < Number(windowMs || 2000)) return false;
    semanticSentAt.set(signature, now);
    return true;
  }

  function productContext(target) {
    const source = (target && target.closest
      ? target.closest('[data-product-handle], [data-product-id], form[action*="/cart/add"], .product-card, .productitem, .product-single')
      : null) || target;
    const handle = source?.getAttribute?.('data-product-handle')
      || source?.querySelector?.('[data-product-handle]')?.getAttribute('data-product-handle')
      || (location.pathname.match(/\/products\/([^/?]+)/i) || [])[1] || '';
    const variantId = source?.getAttribute?.('data-variant-id')
      || source?.querySelector?.('[name="id"]')?.value || '';
    const title = source?.getAttribute?.('data-product-title')
      || source?.querySelector?.('.product-single__title, .card__heading, .product-title, [data-product-title]')?.textContent
      || document.querySelector('.product-single__title, .product-title')?.textContent || '';
    return {
      productHandle: safeText(handle, 160),
      variantId: safeText(variantId, 40),
      productTitle: safeText(title, 160)
    };
  }

  async function refreshCartSnapshot() {
    try {
      // This is Trace's own background probe, not a shopper action. Use the
      // original fetch so a blocked probe never becomes a storefront error.
      const request = nativeFetchRequest || window.fetch.bind(window);
      const response = await request('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (response.ok) {
        const cart = await response.json();
        cartSnapshot = {
          cartValue: Number(cart.total_price || 0) / 100,
          cartCount: Number(cart.item_count || 0),
          lineCount: Array.isArray(cart.items) ? cart.items.length : 0,
          currency: safeText(cart.currency, 8),
          items: Array.isArray(cart.items) ? cart.items.slice(0, 12).map((item) => ({
            handle: safeText(item.handle, 160),
            variantId: safeText(item.variant_id, 40),
            quantity: Number(item.quantity || 0)
          })) : []
        };
      }
    } catch {
      // Keep the last confirmed cart state.
    }
    return cartSnapshot;
  }

  function beginCartAttempt(context, source) {
    const key = `${context.productHandle || ''}:${context.variantId || ''}`;
    if (!semanticAllowed('add_to_cart_attempt', key, 1800)) return;
    const actionId = randomId('cart');
    session.pendingCart = {
      actionId,
      at: Date.now(),
      beforeCount: Number(cartSnapshot.cartCount || 0),
      beforeValue: Number(cartSnapshot.cartValue || 0),
      context,
      source
    };
    persistSession();
    send('add_to_cart_attempt', context.productTitle || 'Add to cart', {
      ...context, actionId, addSource: source, ...cartSnapshot
    }, { priority: true });
    setTimeout(() => confirmCartAttempt(false), 900);
  }

  async function confirmCartAttempt(requestConfirmed) {
    const pending = session.pendingCart;
    if (!pending) return;
    const snapshot = await refreshCartSnapshot();
    const changed = Number(snapshot.cartCount) > Number(pending.beforeCount)
      || Number(snapshot.cartValue) > Number(pending.beforeValue);
    if (requestConfirmed || changed) {
      if (semanticAllowed('add_to_cart', pending.actionId, 10 * 60 * 1000)) {
        send('add_to_cart', pending.context.productTitle || 'Product added', {
          ...pending.context,
          actionId: pending.actionId,
          addSource: requestConfirmed ? 'network_confirmed' : 'cart_state_confirmed',
          ...snapshot
        }, { priority: true });
      }
      session.pendingCart = null;
      persistSession();
    } else if (Date.now() - Number(pending.at) > 2 * 60 * 1000) {
      session.pendingCart = null;
      persistSession();
    }
  }

  async function sendCheckoutOnce(source, target) {
    if (session.checkoutSent) return;
    session.checkoutSent = true;
    persistSession();
    const snapshot = await refreshCartSnapshot();
    send('checkout_start', 'Checkout started', {
      checkoutSource: source,
      element: target?.tagName?.toLowerCase?.() || '',
      ...snapshot
    }, { priority: true });
  }

  function requestInfo(rawUrl) {
    try {
      const parsed = new URL(String(rawUrl || ''), location.href);
      return {
        protocol: parsed.protocol.toLowerCase(),
        hostname: parsed.hostname.toLowerCase(),
        requestOrigin: parsed.origin.slice(0, 180),
        requestPath: parsed.pathname.slice(0, 300),
        sameOrigin: parsed.origin === location.origin
      };
    } catch {
      return {
        protocol: '', hostname: '', requestOrigin: '',
        requestPath: safeText(String(rawUrl || '').split('?')[0], 300), sameOrigin: false
      };
    }
  }

  function isRequestNoise(info) {
    // Browser extensions and in-app browsers expose internal URLs such as
    // properties://ebpaap. They are not requests made by the Shopify store.
    if (info.protocol && info.protocol !== 'http:' && info.protocol !== 'https:') return true;
    const telemetryHosts = [
      'www.google-analytics.com',
      'www.merchant-center-analytics.goog',
      'otlp-http-production.shopifysvc.com',
      'monorail-edge.shopifysvc.com',
      'tracking.aws.judge.me'
    ];
    return telemetryHosts.some((host) => info.hostname === host || info.hostname.endsWith(`.${host}`));
  }

  function isTraceUrl(url) {
    if (!url) return true;
    const candidate = requestInfo(url);
    return [eventEndpoint, liveEndpoint].some((endpoint) => {
      const trace = requestInfo(endpoint);
      return candidate.requestOrigin === trace.requestOrigin && candidate.requestPath === trace.requestPath;
    });
  }

  function recordRequestError(url, method, response, error) {
    if (!started || isTraceUrl(url)) return;
    const info = requestInfo(url);
    if (isRequestNoise(info)) return;
    const status = Number(response?.status || 0);
    const message = safeText(error?.message || (response ? `HTTP ${status}` : 'Network request failed'), 180);
    if (error?.name === 'AbortError' || status === 499 || status === 0 || /abort|cancel/i.test(message)) return;
    const critical = /^\/(?:cart|checkout|account)(?:\/|\.|$)/i.test(info.requestPath);
    if (!critical && status > 0 && status < 400) return;
    const kind = critical ? 'commerce' : (info.sameOrigin ? 'storefront' : 'third_party');
    const key = `${kind}:${String(method || 'GET').toUpperCase()}:${info.requestOrigin}:${info.requestPath}:${status}:${message}`;
    const previous = requestErrors.get(key);
    if (previous && Date.now() - previous.firstAt < errorWindowMs) {
      previous.count += 1;
      previous.lastAt = Date.now();
      return;
    }
    requestErrors.set(key, { firstAt: Date.now(), lastAt: Date.now(), count: 1, message, kind, ...info, status, method });
    send('request_error', message, {
      errorKind: kind,
      requestMethod: String(method || 'GET').toUpperCase(),
      requestStatus: status,
      requestOrigin: info.requestOrigin,
      requestPath: info.requestPath,
      occurrenceCount: 1
    });
  }

  function flushRequestErrorSummaries() {
    requestErrors.forEach((entry) => {
      if (entry.count <= 1) return;
      send('request_error_summary', entry.message, {
        errorKind: entry.kind,
        requestMethod: String(entry.method || 'GET').toUpperCase(),
        requestStatus: entry.status,
        requestOrigin: entry.requestOrigin,
        requestPath: entry.requestPath,
        occurrenceCount: entry.count
      }, { priority: true });
    });
    requestErrors.clear();
  }

  function traceRequest(url, method, response, error) {
    if (!started || isTraceUrl(url)) return;
    const info = requestInfo(url);
    const verb = String(method || 'GET').toUpperCase();
    const cartMatch = info.requestPath.match(/\/cart\/(add|change|update|clear)(?:\.js)?$/i);
    if (cartMatch && verb !== 'GET' && response?.ok) {
      if (cartMatch[1].toLowerCase() === 'add') setTimeout(() => confirmCartAttempt(true), 250);
      else setTimeout(async () => send('cart_update', `Cart ${cartMatch[1].toLowerCase()}`, await refreshCartSnapshot()), 250);
    }
    if (/\/(?:checkout|cart\/checkout)(?:\/|$)/i.test(info.requestPath) && verb !== 'GET' && response?.ok) {
      sendCheckoutOnce('network');
    }
    if (error || (response && !response.ok)) recordRequestError(url, verb, response, error);
  }

  function installNetworkInstrumentation() {
    if (window.__TRACE_NETWORK_V2_INSTALLED__) return;
    window.__TRACE_NETWORK_V2_INSTALLED__ = true;
    const nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      nativeFetchRequest = nativeFetch.bind(window);
      window.fetch = function traceFetch(input, init) {
        const url = typeof input === 'string' ? input : input?.url || '';
        const method = init?.method || input?.method || 'GET';
        return nativeFetch.apply(this, arguments).then((response) => {
          traceRequest(url, method, response);
          return response;
        }).catch((error) => {
          traceRequest(url, method, null, error);
          throw error;
        });
      };
    }
    if (window.XMLHttpRequest && XMLHttpRequest.prototype) {
      const nativeOpen = XMLHttpRequest.prototype.open;
      const nativeSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function traceOpen(method, url) {
        this.__traceMethod = method;
        this.__traceUrl = url;
        return nativeOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function traceSend() {
        this.addEventListener('load', () => traceRequest(this.__traceUrl, this.__traceMethod, {
          status: this.status, ok: this.status >= 200 && this.status < 400
        }));
        this.addEventListener('error', () => traceRequest(this.__traceUrl, this.__traceMethod, null, new Error('Network request failed')));
        this.addEventListener('abort', () => {});
        return nativeSend.apply(this, arguments);
      };
    }
  }

  function heartbeat() {
    if (!started || document.hidden || !allowed()) return;
    send('heartbeat', 'Presence confirmed', { idle, activeMs: session.activeMs, visibleMs: session.visibleMs });
  }

  function rotateSession(reason) {
    send('session_end', reason || 'Session rotated', { reason }, { priority: true });
    session = newSessionState(Date.now(), reason);
    pageViewId = randomId('page');
    idle = false;
    lastClockAt = Date.now();
    persistSession();
    session.opened = true;
    send('session_start', 'New session', { sessionReason: reason });
    send('page_view', location.pathname + location.search);
  }

  function noteActivity() {
    if (!started) return;
    const now = Date.now();
    if (now - Number(session.lastActivityAt || now) > sessionIdleMs || now - Number(session.startedAt || now) > sessionMaxMs) {
      rotateSession(now - Number(session.startedAt || now) > sessionMaxMs ? 'max_duration' : 'idle_timeout');
    }
    updateClocks(now);
    session.lastActivityAt = now;
    if (idle) {
      idle = false;
      send('activity_resume', 'Visitor resumed activity');
    }
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      updateClocks(Date.now());
      idle = true;
      send('idle_start', '30 sec idle');
    }, 30000);
    persistSession();
  }

  function sendInitialContext() {
    const currentType = pageType();
    if (currentType === 'product') send('view_item', 'Product viewed', productContext(document.querySelector('main') || document.body));
    else if (currentType === 'collection') send('collection_view', 'Collection viewed', {
      collectionHandle: safeText((location.pathname.match(/\/collections\/([^/?]+)/i) || [])[1], 160)
    });
    else if (currentType === 'search') {
      const query = new URLSearchParams(location.search);
      send('search', safeText(query.get('q'), 160), { query: safeText(query.get('q'), 160) });
    } else if (currentType === 'cart') send('cart_view', 'Cart viewed', cartSnapshot);
  }

  let pendingCustomer = null;

  function boot() {
    if (started || !allowed()) return;
    started = true;
    installNetworkInstrumentation();
    if (!session.opened) {
      session.opened = true;
      persistSession();
      send('session_start', 'New session', { sessionReason: session.reason });
    }
    send('page_view', location.pathname + location.search);
    if (pendingCustomer && (pendingCustomer.id || pendingCustomer.email)) {
      send('identify', 'Customer identified', {
        customerId: pendingCustomer.id,
        customerName: pendingCustomer.name,
        customerEmail: pendingCustomer.email,
        identitySource: pendingCustomer.source
      }, { priority: true });
    }

    ['mousemove', 'scroll', 'keydown', 'touchstart'].forEach((eventName) => {
      window.addEventListener(eventName, noteActivity, { passive: true });
    });
    window.addEventListener('pointermove', (event) => {
      livePointer = { x: Math.round(event.clientX), y: Math.round(event.clientY) };
    }, { passive: true });
    window.addEventListener('touchstart', (event) => {
      const touch = event.touches && event.touches[0];
      if (touch) livePointer = { x: Math.round(touch.clientX), y: Math.round(touch.clientY) };
    }, { passive: true });

    document.addEventListener('click', (event) => {
      noteActivity();
      const target = event.target && event.target.closest
        ? event.target.closest('[data-trace-label], a, button, input[type="submit"]')
        : null;
      if (!target) return;
      const label = target.dataset.traceLabel
        || target.getAttribute('aria-label')
        || safeText(target.textContent, 80)
        || target.tagName;
      send('click', label, {
        selector: target.tagName.toLowerCase(),
        x: Math.round(event.clientX), y: Math.round(event.clientY),
        viewportWidth: window.innerWidth, viewportHeight: window.innerHeight
      });

      const checkoutSelector = '[name="checkout"], [href*="/checkout"]';
      const checkoutTarget = target.matches(checkoutSelector) ? target : target.closest(checkoutSelector);
      if (checkoutTarget) sendCheckoutOnce('click', checkoutTarget);

      const addSelector = '[name="add"], [data-add-to-cart], [data-action="add-to-cart"], .js-grid-cart';
      const addTarget = target.matches(addSelector) ? target : target.closest(addSelector);
      if (addTarget) beginCartAttempt(productContext(addTarget), 'click');

      const cartSelector = 'a[href*="/cart"], [data-cart-toggle], [aria-controls*="cart" i]';
      if (target.matches(cartSelector) || target.closest(cartSelector)) send('cart_open', 'Cart opened', { source: 'click', ...cartSnapshot });
    }, { passive: true });

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!target || typeof target.matches !== 'function') return;
      if (target.matches('select[name="sort_by"], #SortBy, #SortBy-mobile, #SortBy-sidebar')) {
        send('collection_sort_change', safeText(target.value, 80), { sortBy: safeText(target.value, 80) });
      } else if (target.matches('[name="id"], select[name*="option" i], [data-variant-selector]')) {
        send('variant_change', safeText(target.value, 80), { variantId: safeText(target.value, 40), ...productContext(target) });
      }
    }, { passive: true });

    document.addEventListener('submit', (event) => {
      const form = event.target && event.target.closest ? event.target.closest('form') : null;
      if (!form) return;
      const action = String(form.getAttribute('action') || '');
      if (/\/cart\/add/i.test(action)) beginCartAttempt(productContext(form), 'form');
      else if (form.querySelector('[name="checkout"]')) sendCheckoutOnce('form', form);

      const emailInput = form.querySelector('input[type="email"], input[name*="email" i]');
      const marker = [form.id, form.className, action, form.querySelector('[name="contact[tags]"]')?.value]
        .filter(Boolean).join(' ').toLowerCase();
      const email = String(emailInput?.value || '').trim().toLowerCase();
      if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && /(newsletter|subscribe|marketing)/.test(marker)) {
        pendingCustomer = { id: '', name: '', email, source: 'newsletter' };
        send('identify', 'Newsletter subscriber identified', {
          customerEmail: email,
          identitySource: 'newsletter'
        }, { priority: true });
      }
    }, { passive: true });

    let lastScrollDepth = 0;
    window.addEventListener('scroll', () => {
      const depth = Math.min(100, Math.round((window.scrollY / Math.max(1, document.documentElement.scrollHeight - window.innerHeight)) * 100));
      if (depth >= lastScrollDepth + 25) {
        lastScrollDepth = depth;
        send('scroll', `${depth}% depth`, { depth, viewportWidth: window.innerWidth, viewportHeight: window.innerHeight });
      }
    }, { passive: true });

    document.addEventListener('visibilitychange', () => {
      updateClocks(Date.now());
      send(document.hidden ? 'visibility_hidden' : 'visibility_visible', document.hidden ? 'Tab moved to background' : 'Tab visible again');
      if (!document.hidden) {
        noteActivity();
        heartbeat();
        sendLiveFrame(true);
      } else {
        sendLiveFrame(false);
      }
    });

    window.addEventListener('error', (event) => {
      send('javascript_error', safeText(event.message || 'Unhandled storefront error', 180), {
        errorSource: safeText(event.filename, 300),
        errorLine: Number(event.lineno || 0),
        errorColumn: Number(event.colno || 0)
      });
    });
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason && event.reason.message ? event.reason.message : event.reason;
      send('javascript_error', safeText(reason || 'Unhandled promise rejection', 180), {
        errorSource: 'unhandledrejection'
      });
    });
    window.addEventListener('online', scheduleRetry);
    window.addEventListener('pagehide', () => {
      updateClocks(Date.now());
      clearInterval(heartbeatTimer);
      clearInterval(liveFrameTimer);
      sendLiveFrame(false);
      flushRequestErrorSummaries();
      send('page_exit', 'Page closed or navigated', {
        activeMs: session.activeMs,
        visibleMs: session.visibleMs
      }, { priority: true });
    });

    noteActivity();
    heartbeat();
    startLiveView();
    heartbeatTimer = setInterval(heartbeat, heartbeatIntervalMs);
    refreshCartSnapshot().then(() => {
      sendInitialContext();
      if (session.pendingCart) confirmCartAttempt(false);
    });
  }

  function startWhenAllowed() {
    if (allowed()) boot();
  }

  window.Trace = {
    version: TRACKER_VERSION,
    boot,
    status() {
      return {
        trackerVersion: TRACKER_VERSION,
        schemaVersion: SCHEMA_VERSION,
        sessionId: session.id,
        visitorId,
        started,
        consentAllowed: allowed(),
        queued: pendingRequests.length,
        activeMs: session.activeMs,
        visibleMs: session.visibleMs
      };
    },
    identify(customer) {
      const identity = typeof customer === 'object' ? customer : { id: customer };
      pendingCustomer = {
        id: String(identity.id || '').slice(0, 120),
        name: safeText(identity.name, 120),
        email: String(identity.email || '').slice(0, 180),
        source: safeText(identity.source || (identity.id ? 'shopify_account' : 'newsletter'), 40)
      };
      if (started && (pendingCustomer.id || pendingCustomer.email)) {
        send('identify', 'Customer identified', {
          customerId: pendingCustomer.id,
          customerName: pendingCustomer.name,
          customerEmail: pendingCustomer.email,
          identitySource: pendingCustomer.source
        }, { priority: true });
      }
    }
  };

  document.addEventListener('visitorConsentCollected', startWhenAllowed);
  document.addEventListener('trackingConsentAccepted', startWhenAllowed);
  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], startWhenAllowed);
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWhenAllowed, { once: true });
  } else {
    startWhenAllowed();
  }
})(window.TRACE_CONFIG || {});
